/**
 * ITP-E-304-01 (Plan ITP-P-68 D-D2 / D-D3): the Company 360 directory. Mirrors ITP-API's `AdminDashboardCompaniesQuery`
 * (ITP-E-303): the response dictionary keys are PascalCase, the row properties camelCase (`isCamelCase = true`).
 */

/** A health tier (InsightRules.TierOf: critical < 40 ≤ warning < 65 ≤ watch < 80 ≤ healthy). */
export type AdminDashboardDirectoryTier = 'critical' | 'warning' | 'watch' | 'healthy';

/** The Tier filter: every company, one tier, or the companies with no insight row ("Unscored"). */
export type AdminDashboardDirectoryTierFilter = 'all' | AdminDashboardDirectoryTier | 'unscored';

/** The Kind filter: every company, the Clients scope set, or the companies not in Client Manager. */
export type AdminDashboardDirectoryKind = 'all' | 'clients' | 'nonclients';

/** The Status filter; the directory opens on 'active'. */
export type AdminDashboardDirectoryStatus = 'active' | 'inactive' | 'all';

/** The sortable columns. */
export type AdminDashboardDirectorySort = 'health' | 'lastLogin' | 'activeUsers' | 'name';

/** A sort direction. */
export type AdminDashboardDirectoryDir = 'asc' | 'desc';

/** An open (not snoozed, not handled) flag on a directory row. */
export interface AdminDashboardDirectoryFlag {
  kind: string;
  label: string;
}

/** One directory row: the camelCase mirror of ITP-API's directory row (D-D2). */
export interface AdminDashboardDirectoryRow {
  /** Lowercased. */
  companyId: string;
  companyName: string;
  /** In the Clients scope set. */
  isClient: boolean;
  isActive: boolean;
  /** The firm a sub-account sits under; null for a top-level company. */
  parentCompanyId: string | null;
  /** 0–100 from the latest insight day; null = Unscored. */
  healthScore: number | null;
  /** null = Unscored (no insight row). */
  tier: AdminDashboardDirectoryTier | null;
  openFlags: AdminDashboardDirectoryFlag[];
  /** From the latest daily snapshot; null when the company has none. */
  activeUsers: number | null;
  /** UTC midnight (ISO) of the latest day with logins; null = never logged in. */
  lastLogin: string | null;
}

/** The whole `fieldValues` of `AdminDashboardCompaniesQuery`: one page of rows, the full count, the page served and the insight day. */
export interface AdminDashboardDirectory {
  Rows: AdminDashboardDirectoryRow[];
  Total: number;
  /** 1-based. */
  Page: number;
  /** The server clamps it to 10..100. */
  PageSize: number;
  /** ISO UTC day of the latest insight run; null before the first one. */
  AsOf: string | null;
}

/** What the directory asks for: one member per request field of `AdminDashboardCompaniesQuery` (D-D3). */
export interface AdminDashboardDirectoryParams {
  search: string;
  tier: AdminDashboardDirectoryTierFilter;
  kind: AdminDashboardDirectoryKind;
  flagged: boolean;
  status: AdminDashboardDirectoryStatus;
  sort: AdminDashboardDirectorySort;
  dir: AdminDashboardDirectoryDir;
  page: number;
  pageSize: number;
}

/** The directory as it opens: every active company, worst health first, 50 a page (D-D3 defaults). */
export const ADB_DIRECTORY_DEFAULTS: Readonly<AdminDashboardDirectoryParams> = {
  search: '', tier: 'all', kind: 'all', flagged: false, status: 'active', sort: 'health', dir: 'asc', page: 1, pageSize: 50
};

/** A column's first direction (D-D3): the most recent last login and the most active users first; health and name ascending. */
export function adbDirectoryDefaultDir(sort: AdminDashboardDirectorySort): AdminDashboardDirectoryDir {
  return sort === 'lastLogin' || sort === 'activeUsers' ? 'desc' : 'asc';
}
